import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useBrand } from "@/lib/brand.jsx";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Pill, EmptyState } from "@/components/ui-bits";
import { formatINR, fmtDate, today } from "@/lib/format";
import { BACKEND_URL } from "@/lib/config";
import { Plus, Pencil, Trash2, Download, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Software", "Marketing", "Hosting", "Travel", "Salaries", "Contractors", "Equipment", "Office", "Misc"];
const PMETHODS = ["Bank Transfer", "UPI", "Razorpay", "Cash", "Card", "PayPal", "Other"];

const empty = () => ({
  brand_id: "",
  category: "Software",
  vendor: "",
  description: "",
  date: today(),
  amount: 0,
  gst: 0,
  method: "Bank Transfer",
  receipt_available: false,
  reimbursable: false,
  recurring: false,
  project_id: "",
  notes: "",
});

export default function Expenses() {
  const { brands, brandQuery, activeBrandId } = useBrand();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = useCallback(async () => {
    const [a, p] = await Promise.all([
      api.get("/expenses", { params: brandQuery }),
      api.get("/projects"),
    ]);
    setItems(a.data); setProjects(p.data);
  }, [brandQuery]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty(), brand_id: activeBrandId !== "all" ? activeBrandId : brands[0]?.id || "" });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...empty(), ...e, project_id: e.project_id || "" });
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = {
        ...form,
        amount: Number(form.amount || 0),
        gst: Number(form.gst || 0),
        project_id: form.project_id || null,
      };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Expense updated" : "Expense added");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (e) => {
    if (!window.confirm("Delete expense?")) return;
    await api.delete(`/expenses/${e.id}`); load();
  };

  const uploadReceipt = async (eid, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/expenses/${eid}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt attached"); load();
    } catch { toast.error("Upload failed"); }
  };

  const total = items.reduce((s, e) => s + (e.amount || 0), 0);
  const gstTotal = items.reduce((s, e) => s + (e.gst || 0), 0);

  return (
    <div data-testid="expenses-page">
      <PageHeader
        eyebrow="Books"
        title="Expenses"
        description="Operational outflows across brands. Attach receipts and tag for GST claims."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild className="border-border bg-white">
              <a href={`${BACKEND_URL}/api/exports/expenses?fmt=xlsx`} target="_blank" rel="noreferrer">
                <Download className="w-4 h-4 mr-1.5" /> Export
              </a>
            </Button>
            <Button data-testid="new-expense-btn" onClick={openCreate} className="bg-bronze text-black hover:bg-[#d4af68]">
              <Plus className="w-4 h-4 mr-1.5" /> Add expense
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <div className="rounded-xl border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Total</div><div className="font-display font-semibold text-2xl mt-2">{formatINR(total)}</div></div>
        <div className="rounded-xl border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">GST input</div><div className="font-display font-semibold text-2xl mt-2 text-bronze">{formatINR(gstTotal)}</div></div>
        <div className="rounded-xl border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Entries</div><div className="font-display font-semibold text-2xl mt-2">{items.length}</div></div>
        <div className="rounded-xl border border-border bg-card p-5"><div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Recurring</div><div className="font-display font-semibold text-2xl mt-2">{items.filter((e)=>e.recurring).length}</div></div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No expenses logged" />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Brand</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Category</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Vendor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Description</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Amount</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">GST</TableHead>
                <TableHead className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Receipt</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((e) => {
                const brand = brands.find((b) => b.id === e.brand_id);
                return (
                  <TableRow key={e.id} className="border-border hover:bg-[#f8f6f2]" data-testid={`expense-row-${e.id}`}>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(e.date)}</TableCell>
                    <TableCell>{brand ? <Pill>{brand.code}</Pill> : "—"}</TableCell>
                    <TableCell className="text-sm">{e.category}</TableCell>
                    <TableCell className="text-sm">{e.vendor}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[260px] truncate">{e.description}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatINR(e.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-muted-foreground">{formatINR(e.gst)}</TableCell>
                    <TableCell>
                      {e.receipt_url ? (
                        <a href={`${BACKEND_URL}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-bronze hover:underline text-xs flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> View
                        </a>
                      ) : (
                        <label className="text-xs text-muted-foreground hover:text-bronze cursor-pointer flex items-center gap-1">
                          <Upload className="w-3 h-3" /> Upload
                          <input type="file" className="hidden" onChange={(ev) => ev.target.files?.[0] && uploadReceipt(e.id, ev.target.files[0])} />
                        </label>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => openEdit(e)} className="text-muted-foreground hover:text-bronze"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(e)} className="text-muted-foreground hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="expense-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Brand</Label>
              <Select value={form.brand_id} onValueChange={(v)=>setForm({...form, brand_id:v})}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="Brand" /></SelectTrigger>
                <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e)=>setForm({...form, vendor:e.target.value})} className="bg-white" /></div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e)=>setForm({...form, date:e.target.value})} className="bg-white" /></div>
            <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} className="bg-white" /></div>
            <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} className="bg-white" data-testid="expense-amount" /></div>
            <div><Label>GST</Label><Input type="number" value={form.gst} onChange={(e)=>setForm({...form, gst:e.target.value})} className="bg-white" /></div>
            <div>
              <Label>Payment method</Label>
              <Select value={form.method} onValueChange={(v)=>setForm({...form, method:v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{PMETHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project (optional)</Label>
              <Select value={form.project_id} onValueChange={(v)=>setForm({...form, project_id:v})}>
                <SelectTrigger className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-center gap-6 pt-1">
              <label className="text-xs flex items-center gap-2"><Switch checked={form.recurring} onCheckedChange={(v)=>setForm({...form, recurring:v})} /> Recurring</label>
              <label className="text-xs flex items-center gap-2"><Switch checked={form.reimbursable} onCheckedChange={(v)=>setForm({...form, reimbursable:v})} /> Reimbursable</label>
              <label className="text-xs flex items-center gap-2"><Switch checked={form.receipt_available} onCheckedChange={(v)=>setForm({...form, receipt_available:v})} /> Receipt available</label>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes:e.target.value})} className="bg-white" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="save-expense-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
